import { useMemo } from 'react';
import { CellProps, Column } from 'react-table';
import styled from 'styled-components';
import { IntegrationMessageDTO } from '../../../api/api';
import { SearchColumnFilter, SelectColumnFilter, multiSelectFilterFn } from '../../../blocks/table/TableFilterUtils';
import useTableInstance from '../../../hooks/useTableInstance';
import { formatDateWithTime, sortDateTime } from '../../../utils/dateHandling';
import PayloadCell from './PayloadCell';

export type MessageBox = 'inbox' | 'outbox';

export enum MessageStatus {
  Processed = 'Behandlet',
  FailedPermanently = 'Fejlet permanent',
  Retrying = 'Afventer nyt forsøg',
  Pending = 'Afventer behandling'
}

export interface IntegrationMessageData {
  id: string;
  occurredOn: string;
  messageType: string;
  status: string;
  failCount: number;
  errorMessage: string;
  correlationId: string;
  payload: string;
  /** Raw ISO timestamps, kept off the columns so sorting compares dates and not Danish date strings. */
  occurredOnRaw: string;
  processedDateRaw: string;
  /** The untouched message, handed to the dialog when a row is clicked. */
  message: IntegrationMessageDTO;
}

/** Enough of a guid to recognise it in the table - the dialog has the whole thing. */
const ID_PREVIEW_LENGTH = 6;

/**
 * "TaskService.Integration.Events.WorkTasks.BookingCreatedIntegrationEvent, TaskService"
 * is unreadable in a table cell - show just "BookingCreatedIntegrationEvent". The dialog
 * shows the assembly qualified name in full.
 */
export const shortMessageType = (messageType?: string): string => {
  if (!messageType) return '';

  const withoutAssembly = messageType.split(',')[0].trim();
  const segments = withoutAssembly.split('.');

  return segments[segments.length - 1] || withoutAssembly;
};

export const shortId = (id?: string): string => {
  if (!id) return '-';

  return id.length > ID_PREVIEW_LENGTH ? `${id.slice(0, ID_PREVIEW_LENGTH)}…` : id;
};

export const getMessageStatus = (message: IntegrationMessageDTO): MessageStatus => {
  if (message.processedDate) return MessageStatus.Processed;
  if (message.failedPermanently) return MessageStatus.FailedPermanently;
  if (message.nextAttemptDate) return MessageStatus.Retrying;

  return MessageStatus.Pending;
};

export const toMessageData = (message: IntegrationMessageDTO): IntegrationMessageData => ({
  id: message.id ?? '',
  occurredOn: formatDateWithTime(message.occurredOn),
  messageType: shortMessageType(message.messageType),
  status: getMessageStatus(message),
  failCount: message.failCount ?? 0,
  errorMessage: message.errorMessage ?? '',
  correlationId: message.correlationId ?? '',
  payload: message.payload ?? '',
  occurredOnRaw: message.occurredOn ?? '',
  processedDateRaw: message.processedDate ?? '',
  message
});

/** Cells are laid out with flexbox, so long values have to be clipped or they run into the next column. */
const TextCell = ({ value }: CellProps<IntegrationMessageData, string>) => (
  <ClippedText title={value}>{value || '-'}</ClippedText>
);

const IdCell = ({ value }: CellProps<IntegrationMessageData, string>) => (
  <ClippedText title={value}>{shortId(value)}</ClippedText>
);

/**
 * react-table hands every column a flex grow factor, so a column widens with the window
 * unless resizing is turned off. Every column but the error message is pinned this way -
 * that one is left flexible so it soaks up the leftover width instead of a trailing gap.
 */
const fixedWidth = (width: number) => ({
  width,
  minWidth: width,
  canResize: false
});

/** Sorting a guid puts the rows in no useful order, so those columns only filter. */
const unsortable = { disableSortBy: true } as const;

const useIntegrationMessagesList = (messages: IntegrationMessageDTO[], box: MessageBox) => {
  const data = useMemo(() => messages.map(toMessageData), [messages]);

  const columns = useMemo(() => {
    // The outbox has no correlation id to show, the same way the dialog only lists it for the inbox.
    const correlationIdColumn: Column<IntegrationMessageData>[] =
      box === 'inbox'
        ? [
            {
              Header: 'Corr. id',
              accessor: 'correlationId',
              ...fixedWidth(110),
              ...unsortable,
              Filter: SearchColumnFilter,
              filter: 'text',
              Cell: IdCell
            }
          ]
        : [];

    // "Næste forsøg" is left to the dialog - the status column already says a retry is pending.
    return [
      {
        Header: 'Tidspunkt',
        accessor: 'occurredOn',
        ...fixedWidth(150),
        Filter: SearchColumnFilter,
        filter: 'text',
        sortType: (a, b) => sortDateTime(a.original.occurredOnRaw, b.original.occurredOnRaw)
      },
      {
        Header: 'Type',
        accessor: 'messageType',
        ...fixedWidth(320),
        Filter: SearchColumnFilter,
        filter: 'text',
        Cell: TextCell
      },
      {
        // Only ever a small count, so this is as narrow as a search field gets.
        Header: 'Fejl',
        accessor: 'failCount',
        ...fixedWidth(110),
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Id',
        accessor: 'id',
        ...fixedWidth(100),
        ...unsortable,
        Filter: SearchColumnFilter,
        filter: 'text',
        Cell: IdCell
      },
      ...correlationIdColumn,
      {
        Header: 'Status',
        accessor: 'status',
        ...fixedWidth(140),
        Filter: SelectColumnFilter,
        filter: multiSelectFilterFn,
        Cell: TextCell
      },
      {
        Header: 'Payload',
        accessor: 'payload',
        ...fixedWidth(130),
        ...unsortable,
        Filter: SearchColumnFilter,
        filter: 'text',
        Cell: ({ value }: CellProps<IntegrationMessageData, string>) => <PayloadCell payload={value} />
      },
      {
        // The one column left flexible - long error messages are what this page is read for.
        Header: 'Fejlbesked',
        accessor: 'errorMessage',
        width: 300,
        minWidth: 150,
        Filter: SearchColumnFilter,
        filter: 'text',
        Cell: TextCell
      }
    ] as Column<IntegrationMessageData>[];
  }, [box]);

  const tableInstance = useTableInstance<IntegrationMessageData>(data, columns, {
    initialState: { pageIndex: 0, pageSize: 50, sortBy: [{ id: 'occurredOn', desc: true }] }
  });

  return { tableInstance };
};

const ClippedText = styled.span`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export default useIntegrationMessagesList;
